// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { convertDateToDateOnlyString } from "@insite/client-framework/Common/Utilities/DateUtilities";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import { GetInvoicesApiParameter } from "@insite/client-framework/Services/InvoiceService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import loadInvoices from "@insite/client-framework/Store/Data/Invoices/Handlers/LoadInvoices";
import { getInvoicesDataView } from "@insite/client-framework/Store/Data/Invoices/InvoicesSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import InvoiceDetailPageTypeLink from "@insite/content-library/Components/InvoiceDetailPageTypeLink";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import { DataTableCell } from "@insite/mobius/index";
import { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { CustomAppState } from "@rpsc/App/Store/Reducers";
import { xHasRole, xIsAuthenticated } from "@rpsc/App/Store/Selectors/GeneralSelectors";
import { sharedDateTimeOptions, USER_ROLES } from "@rpsc/XDL/constants";
import * as React from "react";
import { useEffect } from "react";
import { connect, ResolveThunks, useDispatch } from "react-redux";

const enum fields {
    lookBackDays = "lookBackDays",
    numberOfRecords = "numberOfRecords",
    hideHeaderTitle = "hideHeaderTitle",
    viewAllLinkLabel = "viewAllLinkLabel",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.lookBackDays]: number;
        [fields.numberOfRecords]: number;
        [fields.hideHeaderTitle]: boolean;
        [fields.viewAllLinkLabel]: string;
    };
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    const isAuthenticated = xIsAuthenticated(state);
    recentInvoicesParameter.pageSize = ownProps.fields.numberOfRecords;
    recentInvoicesParameter.fromDate = convertDateToDateOnlyString(
        new Date(Date.now() - ownProps.fields.lookBackDays * 60 * 60 * 24 * 1000),
    );
    return {
        invoicesDataView: getInvoicesDataView(state, recentInvoicesParameter),
        invoiceHistoryPageLink: getPageLinkByPageType(state, "InvoiceHistoryPage"),
        canViewInvoices: isAuthenticated && !xHasRole(state, USER_ROLES.REQUISITIONER),
        language: state.context.session.language,
    };
};

const mapDispatchToProps = {
    loadInvoices,
};

export interface RecentInvoicesStyles {
    container?: InjectableCss;
    noInvoicesFoundText?: TypographyPresentationProps;
    dataTable?: DataTableProps;
    dataTableHead?: DataTableHeadProps;
    dataTableHeaders?: DataTableHeaderProps;
    dataTableBody?: DataTableBodyProps;
    dataTableRow?: DataTableRowProps;
    dataTableCells?: DataTableCellBaseProps;
    clickableCell?: LinkPresentationProps;
    dataCells?: DataTableCellProps;
}

export const recentInvoicesStyles: RecentInvoicesStyles = {};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const styles = recentInvoicesStyles;

let recentInvoicesParameter: GetInvoicesApiParameter = {
    customerSequence: "-1",
    page: 1,
    sort: "invoiceDate DESC",
};

const RecentInvoices = ({
    loadInvoices,
    invoicesDataView,
    invoiceHistoryPageLink,
    canViewInvoices,
    language,
    fields: { hideHeaderTitle, viewAllLinkLabel },
    zone,
}: Props) => {
    const dispatch = useDispatch();
    useEffect(() => {
        if (canViewInvoices && !invoicesDataView.value && !invoicesDataView.isLoading) {
            loadInvoices(recentInvoicesParameter);
        } else if (zone.includes("tab")) {
            dispatch({
                type: "xTabs/EnableZone",
                data: {
                    zone,
                    isEnabled: canViewInvoices && !!invoicesDataView.value, // even if its empty array activate the tab
                },
            });
        }
    }, [invoicesDataView.value]);

    if (!canViewInvoices || !invoicesDataView.value) {
        return null;
    }

    const invoiceHistoryUrl = invoiceHistoryPageLink ? invoiceHistoryPageLink.url : undefined;

    return (
        <StyledWrapper {...styles.container}>
            {!hideHeaderTitle &&
                createWidgetElement("XTitledSection/XTitledSection", {
                    fields: {
                        title: translate("Recent Invoices"),
                        variant: "h3",
                        buttonTitle: translate(viewAllLinkLabel),
                        buttonLink: invoiceHistoryUrl,
                        addZone: false,
                        margin: "0 0 16px 0",
                    },
                })}

            {invoicesDataView.value.length === 0 && (
                <Typography {...styles.noInvoicesFoundText}>{siteMessage("Custom_NoInvoicesMessage")}</Typography>
            )}
            {invoicesDataView.value.length > 0 && (
                <DataTable {...styles.dataTable}>
                    <DataTableHead {...styles.dataTableHead}>
                        <DataTableHeader {...styles.dataTableHeaders}>{translate("Invoice #")}</DataTableHeader>
                        <DataTableHeader {...styles.dataTableHeaders}>{translate("Date")}</DataTableHeader>
                        <DataTableHeader {...styles.dataTableHeaders}>{translate("PO / Reference #")}</DataTableHeader>
                        <DataTableHeader {...styles.dataTableHeaders}>{translate("Invoice Total")}</DataTableHeader>
                    </DataTableHead>
                    <DataTableBody {...styles.dataTableBody}>
                        {invoicesDataView.value.map(invoice => {
                            if (!invoice) {
                                return null;
                            }
                            const { invoiceNumber, invoiceDate, customerPO, invoiceTotalDisplay } = invoice;
                            const invoiceDateDisplay = invoiceDate
                                ? getLocalizedDateTime({
                                      dateTime: new Date(invoiceDate),
                                      language,
                                      options: {
                                          ...sharedDateTimeOptions,
                                      },
                                  })
                                : "";
                            return (
                                <DataTableRow key={invoiceNumber}>
                                    <DataTableCell {...styles.clickableCell}>
                                        <InvoiceDetailPageTypeLink
                                            title={invoiceNumber}
                                            invoiceNumber={invoiceNumber}
                                        />
                                    </DataTableCell>
                                    <DataTableCell {...styles.dataCells}>
                                        <Typography>{invoiceDateDisplay}</Typography>
                                    </DataTableCell>
                                    <DataTableCell {...styles.dataCells}>
                                        <Typography>{customerPO}</Typography>
                                    </DataTableCell>
                                    <DataTableCell {...styles.dataCells}>
                                        <Typography>{invoiceTotalDisplay}</Typography>
                                    </DataTableCell>
                                </DataTableRow>
                            );
                        })}
                    </DataTableBody>
                </DataTable>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(RecentInvoices),
    definition: {
        group: "Common",
        icon: "list-ul",
        fieldDefinitions: [
            {
                name: fields.lookBackDays,
                displayName: "Lookback Days",
                editorTemplate: "IntegerField",
                fieldType: "General",
                min: 1,
                defaultValue: 10,
            },
            {
                name: fields.numberOfRecords,
                displayName: "Number of Recent Invoices Displayed",
                editorTemplate: "IntegerField",
                fieldType: "General",
                min: 1,
                max: 12,
                defaultValue: 6,
            },
            {
                name: fields.hideHeaderTitle,
                displayName: "Hide Header Title",
                editorTemplate: "CheckboxField",
                fieldType: "General",
                defaultValue: false,
            },
            {
                name: fields.viewAllLinkLabel,
                editorTemplate: "TextField",
                fieldType: "General",
                defaultValue: "View All Invoices",
                isVisible: item => !item.fields.hideHeaderTitle,
            },
        ],
    },
};

export default widgetModule;
